import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import { buildTheme } from '@/lib/theme'
import type { RootState } from '@/store'

interface ThemeWrapperProps {
  children: React.ReactNode
}

/**
 * Reads themeMode from Redux and feeds a freshly-built MUI theme
 * into ThemeProvider so the whole app re-themes without a page reload.
 *
 * `injectFirst` puts MUI's own styles at the top of <head>, so class names
 * from makeStyles (@emotion/css, appended later) win at equal specificity.
 * Without it MUI defaults such as Typography's font-size/weight silently
 * override component styles.
 */
export function ThemeWrapper({ children }: ThemeWrapperProps) {
  const themeMode = useSelector((state: RootState) => state.ui.themeMode)
  const muiTheme = useMemo(() => buildTheme(themeMode), [themeMode])

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={muiTheme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </StyledEngineProvider>
  )
}
